import React, { useState } from "react";
import { LinkOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import truncate from "lodash.truncate";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";

import {
  CheckoutVariantStatus,
  CheckoutVariantTarget,
  type CheckoutVariant,
} from "@fitness-app/data-models/entities/CheckoutVariant";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import { usePermissionRequirement } from "~/hooks/usePermission";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { supabase } from "~/store/initializeStore";
import { GenerateDiscountCodeModal } from "./GenerateDiscountCodeModal";

type ProductSummary = {
  id: string;
  name: string;
  slug: string | null;
  prices: ProductPrice[] | null;
};

type CheckoutVariantRow = CheckoutVariant & {
  product?: ProductSummary | null;
};

const activeUntilLabel = (activeUntil: number | null) => {
  if (!activeUntil) return <Tag>Bez ograniczeń</Tag>;
  const date = dayjs.unix(activeUntil);
  if (date.isBefore(dayjs())) return <Tag color="red">{date.format("DD.MM.YYYY HH:mm")} wygasł</Tag>;
  return <Tag color="blue">{date.format("DD.MM.YYYY HH:mm")}</Tag>;
};

const priceFormatter = getPriceFormatter("PLN");

export const SalesCheckoutVariants = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [editedVariant, setEditedVariant] = useState<CheckoutVariantRow | null>(null);
  const [activeUntil, setActiveUntil] = useState<Dayjs | null>(null);
  const [codeVariant, setCodeVariant] = useState<CheckoutVariantRow | null>(null);
  const canReadSalesCrm = usePermissionRequirement(dashboardPermissionRequirements.salesCrm);

  const variantsQuery = useQuery({
    queryKey: ["salesCheckoutVariants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checkout_variant")
        .select("*, product:productId(id, name, slug, prices)")
        .eq("status", CheckoutVariantStatus.Active)
        .eq("target", CheckoutVariantTarget.New)
        .order("createdAt", { ascending: false })
        .limit(300);

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CheckoutVariantRow[];
    },
  });

  const updateVariantMutation = useMutation({
    mutationFn: async ({
      variantId,
      patch,
    }: {
      variantId: string;
      patch: Pick<Partial<CheckoutVariant>, "status" | "activeUntil">;
    }) => {
      const { error } = await supabase
        .from("checkout_variant")
        .update({ ...patch, updatedAt: new Date().toISOString() } as never)
        .eq("id", variantId);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditedVariant(null);
      setActiveUntil(null);
      await queryClient.invalidateQueries({ queryKey: ["salesCheckoutVariants"] });
      void message.success("Wariant koszyka zaktualizowany");
    },
  });

  const openActiveUntilModal = (variant: CheckoutVariantRow) => {
    setEditedVariant(variant);
    setActiveUntil(variant.activeUntil ? dayjs.unix(variant.activeUntil) : null);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space wrap align="center" style={{ width: "100%", justifyContent: "space-between" }}>
        <Space direction="vertical" size={0}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Koszyki sprzedażowe
          </Typography.Title>
          <Typography.Text type="secondary">
            Przeglądaj koszyki produktów i kopiuj linki dla klientów. Możesz włączać i wyłączać koszyki, ustawiać datę
            ważności oraz generować kody zniżkowe.
          </Typography.Text>
        </Space>
        {canReadSalesCrm ? (
          <Link to="/sales">
            <Button>Sales CRM</Button>
          </Link>
        ) : null}
      </Space>

      <Card>
        <Table<CheckoutVariantRow>
          rowKey="id"
          dataSource={variantsQuery.data ?? []}
          loading={variantsQuery.isLoading || variantsQuery.isFetching}
          scroll={{ x: true }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
        >
          <Table.Column<CheckoutVariantRow>
            title="Produkt"
            render={(_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.product?.name ?? row.productId}</Typography.Text>
                <Typography.Text type="secondary">{row.product?.slug ?? "-"}</Typography.Text>
              </Space>
            )}
          />
          <Table.Column<CheckoutVariantRow>
            title="Wariant"
            render={(_, row) => (
              <Typography.Paragraph
                copyable={{ text: row.slug, tooltips: [row.slug, "Skopiowano"] }}
                style={{ marginBottom: 0 }}
              >
                {truncate(row.slug, { length: 28 })}
              </Typography.Paragraph>
            )}
          />
          <Table.Column<CheckoutVariantRow>
            title="Utworzony"
            dataIndex="createdAt"
            defaultSortOrder="descend"
            sorter={(a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()}
            render={(createdAt: CheckoutVariant["createdAt"]) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<CheckoutVariantRow>
            title="Ceny"
            dataIndex="prices"
            render={(prices: CheckoutVariant["prices"]) => prices?.length ?? 0}
          />
          <Table.Column<CheckoutVariantRow>
            title="Downselle"
            dataIndex="downsellPrices"
            render={(downsellPrices: CheckoutVariant["downsellPrices"]) => downsellPrices?.length ?? 0}
          />
          <Table.Column<CheckoutVariantRow> title="Odsłony" dataIndex="views" />
          <Table.Column<CheckoutVariantRow> title="Transakcje" dataIndex="purchase" />
          <Table.Column<CheckoutVariantRow>
            title="Przychód"
            dataIndex="revenue"
            render={(revenue) => priceFormatter.format(revenue / 100)}
          />
          <Table.Column<CheckoutVariantRow>
            title="Link"
            render={(_, row) => {
              const link = getProductLink({
                productId: row.productId,
                domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
                slug: row.slug,
                isVariant: true,
                isRenewal: row.target === CheckoutVariantTarget.Renewal,
              });

              return (
                <Tooltip title="Kopiuj link">
                  <Button
                    type="link"
                    icon={<LinkOutlined style={{ fontSize: 20 }} />}
                    onClick={() => {
                      void navigator.clipboard.writeText(link);
                      void message.success("Link skopiowany");
                    }}
                  />
                </Tooltip>
              );
            }}
          />
          <Table.Column<CheckoutVariantRow>
            title="Aktywny"
            dataIndex="status"
            render={(status: CheckoutVariant["status"], row) => (
              <Switch
                checked={status === CheckoutVariantStatus.Active}
                disabled={status === CheckoutVariantStatus.Archived}
                loading={updateVariantMutation.isLoading && updateVariantMutation.variables?.variantId === row.id}
                onChange={(checked) =>
                  updateVariantMutation.mutate({
                    variantId: row.id,
                    patch: {
                      status: checked ? CheckoutVariantStatus.Active : CheckoutVariantStatus.Inactive,
                    },
                  })
                }
              />
            )}
          />
          <Table.Column<CheckoutVariantRow>
            title="Ważny do"
            dataIndex="activeUntil"
            render={(value: CheckoutVariant["activeUntil"], row) => (
              <Space>
                {activeUntilLabel(value)}
                <Button size="small" onClick={() => openActiveUntilModal(row)}>
                  Zmień
                </Button>
              </Space>
            )}
          />
          <Table.Column<CheckoutVariantRow>
            title="Akcje"
            fixed="right"
            render={(_, row) => (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "generate-discount-code",
                      label: "Generuj kod zniżkowy",
                      onClick: () => setCodeVariant(row),
                    },
                  ],
                }}
              >
                <Button>Akcje</Button>
              </Dropdown>
            )}
          />
        </Table>
      </Card>

      <Modal
        title="Data ważności koszyka"
        open={Boolean(editedVariant)}
        onCancel={() => {
          setEditedVariant(null);
          setActiveUntil(null);
        }}
        confirmLoading={updateVariantMutation.isLoading}
        onOk={() => {
          if (!editedVariant) return;
          updateVariantMutation.mutate({
            variantId: editedVariant.id,
            patch: { activeUntil: activeUntil ? activeUntil.unix() : null },
          });
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <DatePicker
            showTime
            allowClear
            value={activeUntil}
            style={{ width: "100%" }}
            onChange={(value) => setActiveUntil(value)}
          />
          <Button onClick={() => setActiveUntil(null)}>Usuń ograniczenie daty</Button>
        </Space>
      </Modal>

      <GenerateDiscountCodeModal variant={codeVariant} onClose={() => setCodeVariant(null)} />
    </Space>
  );
};
